import { useEffect, useState } from "react";
import { MapPin, Phone, Navigation, Clock } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";

const MAPS_DEST = encodeURIComponent("Sagar Darshan Guest House, Prabhas Patan, Somnath, Gujarat 362268");

export default function Location() {
  const [attractions, setAttractions] = useState([]);

  useEffect(() => {
    document.title = "Location & Directions | Sagar Darshan Guest House, Somnath";
    api.attractions().then(setAttractions).catch(() => {});
  }, []);

  return (
    <div data-testid="location-page">
      <PageHero overline="Find Us" title="On the Shores of Somnath"
        image="https://customer-assets-gfyr7b9c.emergentagent.net/job_somnath-coastal-stay/artifacts/0nsdopje_IMG_0888.jpeg" />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHeading number="01" overline="Our Address" title="Find Us" />
            <Reveal delay={0.15}>
              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4">
                  <MapPin size={20} strokeWidth={1.4} className="mt-1 shrink-0 text-gold-dark" />
                  <div>
                    <p className="font-medium text-navy">{SITE.name}</p>
                    <p className="mt-1 text-sm leading-relaxed text-navy/60">{SITE.address}</p>
                    <p className="mt-1 text-xs text-navy/40">{SITE.operator}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={20} strokeWidth={1.4} className="mt-1 shrink-0 text-gold-dark" />
                  <a href={`tel:${SITE.phoneHref}`} data-testid="location-phone" className="text-sm text-navy/70 transition-colors hover:text-gold-dark">{SITE.phoneDisplay}</a>
                </li>
                <li className="flex items-start gap-4">
                  <Clock size={20} strokeWidth={1.4} className="mt-1 shrink-0 text-gold-dark" />
                  <p className="text-sm text-navy/70">Check-in / Check-out: 24-hour basis</p>
                </li>
              </ul>
              <a
                data-testid="get-directions-btn"
                href={`https://www.google.com/maps/dir/?api=1&destination=${MAPS_DEST}`}
                target="_blank" rel="noopener noreferrer"
                className="btn-primary mt-10"
              >
                <Navigation size={14} /> Get Directions
              </a>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-12 border border-navy/10 bg-white p-7">
                <p className="overline-label">Nearby Landmarks</p>
                <ul className="mt-5 space-y-3">
                  {attractions.map((a) => (
                    <li key={a.name} className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-navy/80">{a.name}</span>
                      <span className="shrink-0 text-xs text-gold-dark">{a.distance}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-[11px] text-navy/40">Distances are approximate.</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-3">
            <div className="h-full min-h-[420px] border border-navy/10 bg-white p-2">
              <iframe
                data-testid="google-map-embed"
                title="Sagar Darshan Guest House location map"
                src={`https://www.google.com/maps?q=${MAPS_DEST}&output=embed`}
                className="h-full min-h-[400px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
